import { queryOptions, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { useWriteMutation } from '~/lib/api/write-mutation';
import { queryClient } from '~/lib/query-client';
import { changePassword, fetchSession, login, logout, register, updateProfile } from './auth.api';
import type { ChangePasswordInput, LoginInput, ProfileInput, RegisterInput } from './auth.schemas';
import type { Session, User } from './auth.types';
import { startTokenRotation, stopTokenRotation } from './token-rotation';

export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
};

/**
 * The session is the one piece of state route guards block on, so it is
 * deliberately long-lived in cache: `staleTime: Infinity` means navigating
 * between guarded routes never refetches it. Login, register, and logout write
 * to it directly, and the API client's session-expired event invalidates it.
 */
export const sessionQuery = queryOptions({
  queryKey: authKeys.session(),
  queryFn: fetchSession,
  staleTime: Infinity,
  gcTime: Infinity,
  retry: false,
  refetchOnWindowFocus: false,
});

/** For use in `clientLoader` guards, which run outside React. */
export function ensureSession(): Promise<Session> {
  return queryClient.ensureQueryData(sessionQuery);
}

function applySession(user: User): void {
  queryClient.setQueryData(sessionQuery.queryKey, user);
  startTokenRotation();
}

export function useLogin() {
  return useWriteMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: applySession,
  });
}

export function useRegister() {
  return useWriteMutation({
    mutationFn: (input: RegisterInput) => register(input),
    onSuccess: applySession,
  });
}

/**
 * Both self-service writes land in the session cache directly rather than
 * invalidating it: the response *is* the updated user, and a refetch would only
 * ask the server to repeat what it just said — while every guarded route below
 * suspends on that query in the meantime.
 */
export function useUpdateProfile() {
  const client = useQueryClient();

  return useWriteMutation({
    mutationFn: (input: ProfileInput) => updateProfile(input),
    onSuccess: (user) => {
      client.setQueryData(sessionQuery.queryKey, user);
      // The name shows in the user table too, which is cached elsewhere.
      void client.invalidateQueries({ queryKey: ['config', 'users'] });
    },
  });
}

/** The response carries a fresh token pair, so this device stays signed in. */
export function useChangePassword() {
  return useWriteMutation({
    mutationFn: (input: ChangePasswordInput) => changePassword(input),
    onSuccess: applySession,
  });
}

export function useLogout() {
  const client = useQueryClient();
  const navigate = useNavigate();

  return useWriteMutation({
    mutationFn: logout,
    /**
     * `onSettled`, not `onSuccess`: if the logout call fails we still want the
     * client-side session gone. The alternative — a UI that says you are signed
     * in when the server disagrees — is strictly worse.
     */
    onSettled: () => {
      stopTokenRotation();
      client.setQueryData(sessionQuery.queryKey, null);
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' });
      void navigate('/login', { replace: true });
    },
  });
}
